import React, { useEffect, useMemo, useState } from 'react'
import Modal from '../../Modal'
import SimpleCarousel from '../../ui/SimpleCarousel'
import DiscoverCard from './DiscoverCard'
import { Button, Typography, Avatar, Chip, Container } from '@mui/material'
import { useProject } from '../../../context/project'
import { useGeneral } from '../../../context/general'
import { useSection } from '../../../context/section'
import DiscoverSearch from './DiscoverSearch'
import useDatasets from '../../../pages/visualization/hooks/useDatasets'
import styles from './DiscoverModal.module.scss'


const checkIfActive = (dataset, card) => {
    const { datasetValue, columnValue } = card.dataset
    return !!(dataset?.find(data => data.datasetValue === datasetValue && data.columnValue === columnValue))
}

export default function DiscoverModal({open, handleClose, dataset, limit = 4}) {
    const [ selectedDataset, setSelectedDataset ] = useState([])
    const [ filteredData, setFilteredData ] = useState([])
    const { ticker, topRated } = useProject()
    const { datasets, getColumns } = useDatasets(ticker)
    const { updateDatasets } = useSection()
    const { setPopupAlert } = useGeneral()

    useEffect(() => {
        setSelectedDataset(dataset)
        return () => {}
    },[dataset])

    const datasetType = useMemo(() => [...new Set(datasets?.map(item => item.data_type))],[datasets])

    const cards = useMemo(() => {
        if(datasets?.length && getColumns){
            const cardData = datasets.flatMap(datasetField => {
                return getColumns(datasetField.value)
                    .filter(column => !('monthly_change' in column && column.monthly_change?.change === null))
                        .map(column => 
                        ({ 
                            ...datasetField,
                            dataset: {
                                datasetValue: datasetField.value,
                                datasetName: datasetField.name,
                                columnValue: column.value,
                                columnName: column.name,
                                agg: '',
                                group: '',
                                granularity: datasetField['default_granularity']
                            },
                            description: column.description,
                            dataType: datasetField.data_type,
                            src: datasetField.url,
                            monthlyChange: column.monthly_change?.change,
                            lastUpdated: column.monthly_change?.last_updated
                            }
                        ))
            })
            setFilteredData(cardData)
            return cardData
        }
    }
    ,[datasets, getColumns])

    useEffect(() =>{
        cards && !open && setFilteredData(cards)
        return () => {}
    }, [open, cards])

    const handleCloseDiscoveryModal = () => {
        setSelectedDataset(dataset)
        handleClose()
    }

    const handleRemoveDataset = (data) => setSelectedDataset((chips) => chips.filter((chip) => !(data.datasetValue === chip.datasetValue && data.columnValue === chip.columnValue)))

    const handleAddDataset = (data) => (selectedDataset.length < limit 
        ? setSelectedDataset((chips) => [...chips, data]) 
        : setPopupAlert('error', `You can’t add more than ${limit} Datasets`)
    )

    const handleSubmit = () => {
        updateDatasets(selectedDataset)
        setPopupAlert('success', `Datasets were added successfully`)
        handleClose()
    }

    const carousel = (type, filteredCards, isTopRated) => {
        return ( 
            <SimpleCarousel title={type} key={type}> 
                {filteredCards.map((card, i) => <DiscoverCard isActive={checkIfActive(selectedDataset, card)} key={`${type}-${i}`} handleRemove={handleRemoveDataset} handleUpdate={handleAddDataset} {...card} isTopRated={isTopRated}/>)}
            </SimpleCarousel>
        )
    }

    const topRatedCard = useMemo(() => filteredData?.filter((card) => topRated && topRated[card.dataset.datasetValue] === card.dataset.columnValue ), [filteredData, topRated])

    const chips = useMemo(() => {
        return (
            <div key='discover-chips'>
                <div className={styles['chips']}>
                    {selectedDataset.length 
                        ? selectedDataset.map((item, index ) => 
                            <Chip key={`${item.datasetName}-${item.columnName}-${index}`} 
                                avatar={<Avatar classes={{root: styles['inner-avatar']}}>{item.datasetName}</Avatar>}
                                label={`${item.columnName}`}
                                classes={{root: styles['chip']}}
                                onDelete={() => handleRemoveDataset(item)} 
                            />)
                        : <Typography classes={{root: styles['text']}} variant="h4">You haven't selected any Alternative KPIs yet.</Typography>}
                </div>
            </div>
        )
    },[selectedDataset, limit]) // eslint-disable-line

    const buttons = [
        <Button key="discover-cancel" variant="outlined" onClick={handleCloseDiscoveryModal}>Cancel</Button>,
        <Button key="discover-add" variant="contained" onClick={handleSubmit} color="primary">Update</Button>,
    ]
    const discoverTitle = <Container classes={{root:styles["discover-title-container"]}}><Typography variant="h2" >Discover</Typography><Typography variant="h3" style={{lineHeight:"1.9",fontWeight:"600"}}>{`(${selectedDataset?.length ?? 0}/${limit} selected)`}</Typography></Container>
    return (
         <Modal open={open} handleClose={handleCloseDiscoveryModal} title={discoverTitle} buttons={buttons} limitWidth={false} subheader={<DiscoverSearch data={cards} updatefilteredData={setFilteredData}/>}> 
            <div className={styles['modal-conatiner']}>
                {chips}
                {carousel(`Top Rated Data for ${ticker}`, topRatedCard, true)}
                {datasetType.map(type => {
                    const filteredCards = filteredData.filter(card => card.dataType === type)
                    return carousel(type, filteredCards)
                  })
                }
            </div>
        </Modal>
    )
}